import { useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import FullLoader from 'components/Loader/FullLoader';
import { useNavigationTracker } from 'hooks/common/useNavigationTracker';
import { setNavigationRef } from 'utils/router/navigation';
import {
  clearAuthSessionStorage,
  enforceSessionIdleOnBootstrap,
} from 'utils/sessionIdle';

function useSessionIdleBootstrap(): boolean {
  // The lazy initializer runs exactly once on mount, synchronously, before the
  // first render — making it safe for this side effect. A useEffect would fire
  // after the render, briefly showing protected content to an expired session.
  return useState(() => {
    if (enforceSessionIdleOnBootstrap() === 'expired') {
      clearAuthSessionStorage();
      return true;
    }
    return false;
  })[0];
}

export function PrivateRoute() {
  const sessionIdleExpired = useSessionIdleBootstrap();
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  // Register navigate so axios interceptors can perform SPA navigation
  // (e.g. redirect to /not-found on a 404 without a full page reload).
  useEffect(() => {
    setNavigationRef(navigate);
  }, [navigate]);
  const location = useLocation();

  // Track SPA route changes for authenticated users. Placed here because all
  // private routes render through PrivateRoute, giving us a single mount point
  // inside the React Router context without touching every page component.
  useNavigationTracker();

  if (sessionIdleExpired) {
    return <Navigate to="/login" replace />;
  }

  if (loading) return <FullLoader id="private-route-loader" />;

  return isAuthenticated ? (
    <Outlet />
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
}
